import { Component, Injectable } from '@angular/core'
import { FormGroup, FormBuilder } from '@angular/forms'
import { Router, ActivatedRoute, CanDeactivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router'
import { BaseComponent } from '../../../components/base.component'
import { Customer, Account, Languages, LanguageCode } from '../../../types'
import { CustomerActions } from '../../../actions/customer-actions'
import { possessive, isAgencySignup } from '../../../utils'
import { additionalFields } from '../../../content/customer-content'
import sweetAlert from '../../../bmAlert'
import { Title } from '@angular/platform-browser'

@Component({
  selector: 'audience-and-voice-page-v1',
  template: `
    <split-view>
    <div left>
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-12 mt-3">
            <div class="nc-section-icon fas fa-comment"></div>
            <h1 class="nc-section-header">
              About {{ customer?.businessName }}
            </h1>

            <p *ngIf="!isAgencySignup()" class="top-buffer">Tell us about your business.</p>
            <p *ngIf="!isAgencySignup()" class="top-buffer">Your business and brand are unique. Tell us about your business so we can write blogs that fit your brand voice.</p>
            <p *ngIf="isAgencySignup()" class="top-buffer">Your client's business and brand are unique. Tell us about them so we can write blogs that fit their voice.</p>
            <about-nav-v1 [customer]="customer"></about-nav-v1>
          </div>
        </div>
      </div>
    </div>
      <div right>
        <div class="container-fluid big-top-buffer">
          <div class="row">
            <div class="col-sm-12">
              <h2>{{account?.agency ? possessive(customer?.businessName) : 'Your'}} Audience and Voice</h2>
              <p class="top-buffer bottom-buffer">Help our writers understand what you're looking for in your content:</p>

              <form [formGroup]="form">
                <fieldset class="form-group">
                  <label for="audience">
                    Who's {{isAgencySignup() ? possessive(customer?.businessName) : 'your'}} audience?
                    <i class="fas fa-info-circle" [tooltip]="audienceTooltip" container="body" containerClass="white-tooltip" placement="bottom"></i>
                  </label>
                  <textarea id="audience" class="form-control" formControlName="audience" rows="4" [placeholder]="audiencePlaceholder"></textarea>
                </fieldset>

                <fieldset class="form-group">
                  <label for="voiceAndPersonality">
                    Describe {{isAgencySignup() ? possessive(customer?.businessName) : 'your'}} brand voice & personality:
                    <i class="fas fa-info-circle" [tooltip]="voiceAndPersonalityTooltip" container="body" containerClass="white-tooltip"></i>
                  </label>
                  <textarea id="voiceAndPersonality" class="form-control" formControlName="voiceAndPersonality" rows="4" [placeholder]="voiceAndPersonalityPlaceholder"></textarea>
                </fieldset>

                <fieldset class="form-group">
                  <label for="languageCode">English Type:</label>
                  <select id="languageCode" class="form-control" formControlName="languageCode">
                    <option *ngFor="let lang of languageOptions" [ngValue]="lang.key">{{ lang.value }}</option>
                  </select>
                </fieldset>
              </form>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">

            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="top-buffer bottom-buffer">
                <button (click)="updateCustomer()" class="btn btn-primary mr-2">Save & continue</button>
                <button (click)="next()" class="btn btn-default">Skip for now</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </split-view>
  `,
  styles: [`
    .form-group {
      margin-top: 10px;
    }
    label i.fas {
      margin-left: 3px;
    }
    select {
      font-weight: bold;
    }
  `]
})
export class AudienceAndVoicePageV1 extends BaseComponent {
  customer: Customer;
  private customerId: number;
  account: Account;
  form: FormGroup;
  submitted: boolean = false;
  languageOptions: Array<{key: LanguageCode, value: string}> = [];

  constructor(private route: ActivatedRoute,
    private fb: FormBuilder,
    private Router: Router,
    private customerActions: CustomerActions,
    private titleService: Title) {
    super()
  }

  ngOnInit(): void {
    this.titleService.setTitle('Sign Up - Audience & Voice')
    const routeObs = this.route.params.subscribe(params => {
      this.customerId = +params['customerId']
      this.customerActions.loadCustomer(this.customerId)

      // Fetch the customer and observe changes
      this.observeCustomer(this.customerId, c => {
        this.customer = c
        this.resetForm()
      })
    })
    this.observeAccount(a => this.account = a)
    this.observers.push(routeObs)
    this.setLanguageOptions()
  }

  resetForm(): void {
    this.form = this.fb.group({
      audience: [this.customer ? this.customer.audience : '', null],
      voiceAndPersonality: [this.customer ? this.customer.voiceAndPersonality : '', null],
      languageCode: [this.customer ? this.customer.languageCode : 'en-US', null]
    })
  }

  updateCustomer(): void {
    this.submitted = true
    this.customerActions.updateCustomer(this.customerId, this.form.value).then(() => {
      this.next()
    })
  }

  setLanguageOptions(): void {
    const codes = <LanguageCode[]>Object.keys(Languages)
    this.languageOptions = codes.map(code => { return {key: code, value: Languages[code]} })
  }

  next(): void {
    this.Router.navigate(['../', 'other_info'], { relativeTo: this.route })
  }

  isAgencySignup(): boolean {
    return isAgencySignup(this.account)
  }

  isDirty(): boolean {
    return this.form.dirty
  }

  possessive(s: string): string {
    return possessive(s)
  }

  audienceTooltip: string = additionalFields.audience.tip;
  audiencePlaceholder: string = additionalFields.audience.placeholder;
  voiceAndPersonalityTooltip: string = additionalFields.voiceAndPersonality.tip;
  voiceAndPersonalityPlaceholder: string = additionalFields.voiceAndPersonality.placeholder;
}

@Injectable()
export class CanDeactivateAudienceAndVoicePageV1 implements CanDeactivate<AudienceAndVoicePageV1> {
  canDeactivate(component: AudienceAndVoicePageV1, route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> | boolean {
    if (component.customer && component.isDirty() && !component.submitted) {
      return sweetAlert.confirm({text: "You have unsaved changes, are you sure you want to continue?"})
    } else {
      return true
    }
  }
}
